import { RoofSvgThumbnailProps } from "../types";
import {
  PANEL_POSITIONS,
  SKIRT_PIECES,
  SPIDER_FRAME_COLOR,
  EMPTY_POSITION_COLOR,
  PANEL_COLORS,
  ROOF_DIMENSIONS,
  SPIDER_SPOKE_ANGLES,
  SVG_THUMBNAIL_LAYOUT,
  SVG_THUMBNAIL_ELECTRODES,
} from "../constants";
import { PART_TYPE_IDS } from "../../../models";
import { RoofUIColors } from "../../../theme/colors";

// Helper to create SVG arc path
const createArcPath = (
  cx: number,
  cy: number,
  innerRadius: number,
  outerRadius: number,
  startAngle: number,
  endAngle: number,
  scale: number,
): string => {
  const ir = innerRadius * scale;
  const or = outerRadius * scale;

  // Handle wrap-around angles (e.g., 357° to 55°)
  let effectiveEndAngle = endAngle;
  if (endAngle < startAngle) {
    effectiveEndAngle = endAngle + Math.PI * 2;
  }

  // Start points
  const startInnerX = cx + Math.cos(startAngle) * ir;
  const startInnerY = cy + Math.sin(startAngle) * ir;
  const startOuterX = cx + Math.cos(startAngle) * or;
  const startOuterY = cy + Math.sin(startAngle) * or;

  // End points
  const endInnerX = cx + Math.cos(effectiveEndAngle) * ir;
  const endInnerY = cy + Math.sin(effectiveEndAngle) * ir;
  const endOuterX = cx + Math.cos(effectiveEndAngle) * or;
  const endOuterY = cy + Math.sin(effectiveEndAngle) * or;

  // Determine if arc is larger than 180 degrees
  const arcSpan = effectiveEndAngle - startAngle;
  const largeArcFlag = arcSpan > Math.PI ? 1 : 0;

  // Build path
  return `
    M ${startInnerX} ${startInnerY}
    A ${ir} ${ir} 0 ${largeArcFlag} 1 ${endInnerX} ${endInnerY}
    L ${endOuterX} ${endOuterY}
    A ${or} ${or} 0 ${largeArcFlag} 0 ${startOuterX} ${startOuterY}
    Z
  `;
};

/** Position ID for the Delta Ring in PANEL_POSITIONS */
const DELTA_RING_POSITION_ID = 7;

// Fixed internal size for consistent calculations - actual display size controlled by width/height
const INTERNAL_SIZE = 200;

export const RoofSvgThumbnail = ({
  roofData,
  size = 80,
}: RoofSvgThumbnailProps) => {
  // Use fixed internal coordinates for stable rendering
  const cx = INTERNAL_SIZE / 2;
  const cy = INTERNAL_SIZE / 2;
  const scale = (INTERNAL_SIZE / 2) * SVG_THUMBNAIL_LAYOUT.SCALE_FACTOR;

  // Map location data by positionId for quick lookup
  const locationDataByPositionId = new Map<number, boolean>();
  // Map location data by partTypeId for quick lookup
  const locationDataByPartTypeId = new Map<number, boolean>();

  for (const location of roofData.locations) {
    if (location.PositionId) {
      locationDataByPositionId.set(
        location.PositionId,
        location.HasAssignedPart ?? false,
      );
    }
    if (location.PartTypeId) {
      locationDataByPartTypeId.set(
        location.PartTypeId,
        location.HasAssignedPart ?? false,
      );
    }
  }

  // Check if the elbow area has any part installed (Elbow or Shield)
  // PartTypeId 13 = FOURTH_HOLE_ELBOW, PartTypeId 22 = ELBOW_SHIELD
  const hasElbowInstalled =
    (locationDataByPartTypeId.get(PART_TYPE_IDS.FOURTH_HOLE_ELBOW) ?? false) ||
    (locationDataByPartTypeId.get(PART_TYPE_IDS.ELBOW_SHIELD) ?? false);

  // Get panel color based on whether it has an assigned part
  // Colors are looked up by positionId, not partTypeId
  const getPanelColor = (positionId: number): string => {
    // For elbow position (positionId 3), check if either Elbow or Shield is installed
    if (positionId === 3) {
      return hasElbowInstalled
        ? (PANEL_COLORS[positionId] ?? EMPTY_POSITION_COLOR)
        : EMPTY_POSITION_COLOR;
    }
    const hasAssignedPart = locationDataByPositionId.get(positionId) ?? false;
    if (!hasAssignedPart) {
      return EMPTY_POSITION_COLOR;
    }
    return PANEL_COLORS[positionId] ?? EMPTY_POSITION_COLOR;
  };

  // Check if Spider (position 1) has an assigned part
  const hasSpiderInstalled = locationDataByPositionId.get(1) ?? false;
  const spiderFrameColor = hasSpiderInstalled
    ? SPIDER_FRAME_COLOR
    : EMPTY_POSITION_COLOR;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${INTERNAL_SIZE} ${INTERNAL_SIZE}`}
    >
      {/* Background circle */}
      <circle
        cx={cx}
        cy={cy}
        r={scale * ROOF_DIMENSIONS.outerRingOuter}
        fill={RoofUIColors.SVG_BACKGROUND}
      />

      {/* Render skirt pieces first (outer band) */}
      {SKIRT_PIECES.map((position) => {
        const color = getPanelColor(position.positionId);
        const path = createArcPath(
          cx,
          cy,
          position.innerRadius,
          position.outerRadius,
          position.startAngle,
          position.endAngle,
          scale,
        );

        return (
          <path
            key={`skirt-${position.id}`}
            d={path}
            fill={color}
            stroke={RoofUIColors.SVG_STROKE}
            strokeWidth={SVG_THUMBNAIL_LAYOUT.STROKE_WIDTH}
          />
        );
      })}

      {/* Render panel segments */}
      {PANEL_POSITIONS.map((position) => {
        // Skip Delta Ring for now, render separately
        if (position.positionId === DELTA_RING_POSITION_ID) return null;
        // Skip 4th Hole Elbow, render as oval separately
        if (position.shape === "oval") return null;

        const color = getPanelColor(position.positionId);
        const path = createArcPath(
          cx,
          cy,
          position.innerRadius,
          position.outerRadius,
          position.startAngle,
          position.endAngle,
          scale,
        );

        return (
          <path
            key={position.id}
            d={path}
            fill={color}
            stroke={RoofUIColors.SVG_STROKE}
            strokeWidth={SVG_THUMBNAIL_LAYOUT.STROKE_WIDTH}
          />
        );
      })}

      {/* Render 4th Hole Elbow as an oval */}
      {(() => {
        const elbow = PANEL_POSITIONS.find((p) => p.shape === "oval");
        if (
          !elbow ||
          !elbow.ovalRadiusX ||
          !elbow.ovalRadiusY ||
          elbow.ovalCenterAngle === undefined ||
          !elbow.ovalCenterRadius
        )
          return null;

        // Use combined elbow check (Elbow + Shield)
        // Color by positionId (3 = 4th Hole Elbow position)
        const color = hasElbowInstalled
          ? PANEL_COLORS[elbow.positionId]
          : EMPTY_POSITION_COLOR;

        // Calculate center position
        const elbowCx =
          cx + Math.cos(elbow.ovalCenterAngle) * elbow.ovalCenterRadius * scale;
        const elbowCy =
          cy + Math.sin(elbow.ovalCenterAngle) * elbow.ovalCenterRadius * scale;

        return (
          <ellipse
            cx={elbowCx}
            cy={elbowCy}
            rx={elbow.ovalRadiusX * scale}
            ry={elbow.ovalRadiusY * scale}
            fill={color}
            stroke={RoofUIColors.SVG_STROKE}
            strokeWidth={SVG_THUMBNAIL_LAYOUT.STROKE_WIDTH}
          />
        );
      })()}

      {/* Delta Ring (two concentric rings with electrode holes) */}
      {(() => {
        const deltaRing = PANEL_POSITIONS.find(
          (p) => p.positionId === DELTA_RING_POSITION_ID,
        );
        if (!deltaRing) return null;

        const hasAssignedPart =
          locationDataByPositionId.get(deltaRing.positionId) ?? false;
        // Color by positionId (7 = Delta Ring position)
        const color = hasAssignedPart
          ? PANEL_COLORS[deltaRing.positionId]
          : EMPTY_POSITION_COLOR;

        const ringThickness = SVG_THUMBNAIL_LAYOUT.RING_THICKNESS;

        // Electrode holes configuration (3 holes in triangular arrangement)
        // Two holes near 4th Hole Panel (top, with gap), one opposite (bottom)
        const electrodeRadius = SVG_THUMBNAIL_ELECTRODES.RADIUS_FACTOR * scale;
        const electrodeDistance =
          SVG_THUMBNAIL_ELECTRODES.DISTANCE_FACTOR * scale;
        // Position top holes with a gap between them (similar gap to bottom hole)
        const topHoleAngleOffset =
          (SVG_THUMBNAIL_ELECTRODES.TOP_HOLE_ANGLE_OFFSET * Math.PI) / 180;
        const electrodeAngles = [
          Math.PI / 2 + topHoleAngleOffset, // Top-left - near 4th Hole Panel
          Math.PI / 2 - topHoleAngleOffset, // Top-right - near 4th Hole Panel
          -Math.PI / 2, // Bottom (270°) - opposite to 4th Hole Panel
        ];

        return (
          <>
            {/* Center background (gray area) */}
            <circle
              cx={cx}
              cy={cy}
              r={scale * ROOF_DIMENSIONS.deltaRingInner - ringThickness}
              fill={RoofUIColors.SVG_CENTER_AREA}
            />
            {/* Three electrode holes (dark circles) */}
            {electrodeAngles.map((angle, index) => (
              <circle
                key={`electrode-${index}`}
                cx={cx + Math.cos(angle) * electrodeDistance}
                cy={cy + Math.sin(angle) * electrodeDistance}
                r={electrodeRadius}
                fill={RoofUIColors.SVG_BACKGROUND}
              />
            ))}
            {/* Inner ring */}
            <circle
              cx={cx}
              cy={cy}
              r={scale * ROOF_DIMENSIONS.deltaRingInner}
              fill="none"
              stroke={color}
              strokeWidth={ringThickness}
            />
            {/* Outer ring */}
            <circle
              cx={cx}
              cy={cy}
              r={scale * ROOF_DIMENSIONS.deltaRingOuter}
              fill="none"
              stroke={color}
              strokeWidth={ringThickness}
            />
          </>
        );
      })()}

      {/* Spider frame rings */}
      {/* Outer ring (at panel edge) */}
      <circle
        cx={cx}
        cy={cy}
        r={scale * ROOF_DIMENSIONS.outerRingOuter}
        fill="none"
        stroke={spiderFrameColor}
        strokeWidth={SVG_THUMBNAIL_LAYOUT.OUTER_FRAME_STROKE}
      />
      {/* Inner panel ring (where panels start) */}
      <circle
        cx={cx}
        cy={cy}
        r={scale * ROOF_DIMENSIONS.innerRingInner}
        fill="none"
        stroke={spiderFrameColor}
        strokeWidth={SVG_THUMBNAIL_LAYOUT.INNER_FRAME_STROKE}
      />

      {/* Spider frame radial spokes - using actual segment boundaries */}
      {SPIDER_SPOKE_ANGLES.map((angle, index) => {
        const innerR = scale * ROOF_DIMENSIONS.deltaRingOuter;
        const outerR = scale * ROOF_DIMENSIONS.outerRingOuter;

        return (
          <line
            key={`spoke-${index}`}
            x1={cx + Math.cos(angle) * innerR}
            y1={cy + Math.sin(angle) * innerR}
            x2={cx + Math.cos(angle) * outerR}
            y2={cy + Math.sin(angle) * outerR}
            stroke={spiderFrameColor}
            strokeWidth={SVG_THUMBNAIL_LAYOUT.SPOKE_STROKE}
          />
        );
      })}
    </svg>
  );
};
